/**
 * AgentRegisterForm — "Register Agent" (F031, form-spec `FS-F031-AGENT`, A-01).
 *
 * Registers a customer AI agent as a first-class governed asset: computes its risk tier,
 * enforces the production-placement guard, and projects it into the F030 Governance Graph.
 * `POST /api/portal/agents`.
 *
 * Pattern: `react-hook-form` + `zodResolver`, schema imported from `AgentRegister.schema.ts`
 * (never redefined), Fluent `Field` for verbatim `V-` messages, server errors mapped via the
 * shared `applyServerErrors` (Charter M3 / form-standard.context.md). The outer `Dialog` chrome is
 * owned by `AgentRegistryPage.tsx` (`src/pages/**`) — this component renders the form CONTENT only
 * and keeps its existing `onSuccess`/`onCancel`/`onDirtyChange` prop contract so the hosting page
 * needs no change.
 */
import { useState } from 'react';
import type { FC } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Dropdown, Field, Input, Option, Switch, Textarea, makeStyles, tokens } from '@fluentui/react-components';
import { AddRegular } from '@fluentui/react-icons';
import { useMutation } from '@tanstack/react-query';
import { agentsApi } from '../../../services/api';
import type { Agent, AgentRegistrationInput } from '../../../types';
import { applyServerErrors, useReportFormDirty, FormActions, ServerErrorBanner } from '../../../components/forms';
import { AGENT_ENVIRONMENT_OPTIONS, DATA_ACCESS_LEVEL_OPTIONS, TOOL_ACCESS_LEVEL_OPTIONS, CapabilitiesTagInput } from './agentFormFields';
import { AgentRegisterSchema, type AgentRegisterFormValues, type AgentRegisterFormOutput } from './AgentRegister.schema';

const useStyles = makeStyles({
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
});

// Known request-DTO field keys — used by the server-error -> field mapping (B-03/A-01).
const KNOWN_FIELDS = [
  'agentName',
  'agentVersion',
  'purpose',
  'ownerId',
  'ownerTeam',
  'environment',
  'modelProvider',
  'modelName',
  'runtimeProvider',
  'dataAccessLevel',
  'toolAccessLevel',
  'mcpEnabled',
  'capabilities',
  'metadataJson',
] as const;

const DEFAULT_VALUES: AgentRegisterFormValues = {
  agentName: '',
  agentVersion: '',
  purpose: '',
  ownerId: '',
  ownerTeam: '',
  environment: 'Development',
  modelProvider: '',
  modelName: '',
  runtimeProvider: '',
  dataAccessLevel: 'None',
  toolAccessLevel: 'None',
  mcpEnabled: false,
  capabilities: [],
  metadataJson: '',
};

export interface AgentRegisterFormProps {
  onSuccess?: (agent: Agent) => void;
  onCancel?: () => void;
  /** B-04 — reports dirty state up so the hosting dialog can guard an unsaved-changes dismiss. */
  onDirtyChange?: (dirty: boolean) => void;
}

export const AgentRegisterForm: FC<AgentRegisterFormProps> = ({ onSuccess, onCancel, onDirtyChange }) => {
  const styles = useStyles();

  const {
    control,
    handleSubmit,
    setError,
    reset,
    watch,
    formState: { errors, isDirty },
  } = useForm<AgentRegisterFormValues, unknown, AgentRegisterFormOutput>({
    resolver: zodResolver(AgentRegisterSchema),
    mode: 'onTouched',
    defaultValues: DEFAULT_VALUES,
  });

  useReportFormDirty(isDirty, onDirtyChange);

  const environment = watch('environment');

  const [globalError, setGlobalError] = useState<string | undefined>();

  const registerMutation = useMutation({
    mutationFn: (input: AgentRegistrationInput) => agentsApi.register(input),
    onSuccess: (agent) => {
      reset(DEFAULT_VALUES);
      onSuccess?.(agent);
    },
    onError: (error) => {
      const { globalMessage } = applyServerErrors(error, KNOWN_FIELDS, setError);
      setGlobalError(globalMessage);
    },
  });

  const onValid = (values: AgentRegisterFormOutput) => {
    // B-02 — guard double-submit.
    if (registerMutation.isPending) return;
    setGlobalError(undefined);

    const input: AgentRegistrationInput = {
      agentName: values.agentName,
      agentVersion: values.agentVersion,
      purpose: values.purpose,
      ownerId: values.ownerId,
      ownerTeam: values.ownerTeam,
      environment: values.environment,
      modelProvider: values.modelProvider,
      modelName: values.modelName,
      runtimeProvider: values.runtimeProvider,
      dataAccessLevel: values.dataAccessLevel,
      toolAccessLevel: values.toolAccessLevel,
      mcpEnabled: values.mcpEnabled,
      capabilities: values.capabilities,
      metadataJson: values.metadataJson,
    };

    registerMutation.mutate(input);
  };

  return (
    // `noValidate` — Fluent's `Field required` clones `required`/`aria-required` onto the child
    // control, which would otherwise let the browser's native constraint validation intercept an
    // empty-required submit before react-hook-form's own validation runs.
    <form onSubmit={handleSubmit(onValid)} className={styles.form} noValidate>
      <ServerErrorBanner title="Unable to Register Agent" message={globalError} testId="agent-register-form-server-error" />

      {/* C-01 — Environment = Production advisory. */}
      {environment === 'Production' && (
        <ServerErrorBanner
          title="Production Placement"
          message="Registering computes risk and enforces the production-placement guard."
          testId="agent-register-form-production-warning"
          intent="warning"
        />
      )}

      <Field
        label="Agent Name"
        required
        validationState={errors.agentName ? 'error' : undefined}
        validationMessage={errors.agentName?.message}
      >
        <Controller
          name="agentName"
          control={control}
          render={({ field }) => (
            <Input
              {...field}
              data-testid="agent-register-form-agentName"
              value={field.value ?? ''}
              placeholder="Human-readable agent name"
              disabled={registerMutation.isPending}
              maxLength={200}
            />
          )}
        />
      </Field>

      <Field
        label="Agent Version"
        required
        validationState={errors.agentVersion ? 'error' : undefined}
        validationMessage={errors.agentVersion?.message}
      >
        <Controller
          name="agentVersion"
          control={control}
          render={({ field }) => (
            <Input
              {...field}
              data-testid="agent-register-form-agentVersion"
              value={field.value ?? ''}
              placeholder="e.g. 1.4.0"
              disabled={registerMutation.isPending}
              maxLength={50}
            />
          )}
        />
      </Field>

      <Field
        label="Purpose"
        required
        validationState={errors.purpose ? 'error' : undefined}
        validationMessage={errors.purpose?.message}
      >
        <Controller
          name="purpose"
          control={control}
          render={({ field }) => (
            <Textarea
              {...field}
              data-testid="agent-register-form-purpose"
              value={field.value ?? ''}
              placeholder="What this agent does and why"
              disabled={registerMutation.isPending}
              resize="vertical"
            />
          )}
        />
      </Field>

      <Field
        label="Owner Id"
        required
        validationState={errors.ownerId ? 'error' : undefined}
        validationMessage={errors.ownerId?.message}
      >
        <Controller
          name="ownerId"
          control={control}
          render={({ field }) => (
            <Input
              {...field}
              data-testid="agent-register-form-ownerId"
              value={field.value ?? ''}
              placeholder="Owning user / principal id"
              disabled={registerMutation.isPending}
              maxLength={200}
            />
          )}
        />
      </Field>

      <Field
        label="Owner Team"
        validationState={errors.ownerTeam ? 'error' : undefined}
        validationMessage={errors.ownerTeam?.message}
      >
        <Controller
          name="ownerTeam"
          control={control}
          render={({ field }) => (
            <Input
              {...field}
              data-testid="agent-register-form-ownerTeam"
              value={field.value ?? ''}
              placeholder="Owning team (optional)"
              disabled={registerMutation.isPending}
              maxLength={200}
            />
          )}
        />
      </Field>

      <Field
        label="Environment"
        required
        validationState={errors.environment ? 'error' : undefined}
        validationMessage={errors.environment?.message}
      >
        <Controller
          name="environment"
          control={control}
          render={({ field }) => (
            <Dropdown
              value={field.value}
              selectedOptions={[field.value]}
              disabled={registerMutation.isPending}
              onOptionSelect={(_, data) => field.onChange(data.optionValue ?? field.value)}
              onBlur={field.onBlur}
              data-testid="agent-register-form-environment"
            >
              {AGENT_ENVIRONMENT_OPTIONS.map((v) => (
                <Option key={v} value={v}>
                  {v}
                </Option>
              ))}
            </Dropdown>
          )}
        />
      </Field>

      <Field
        label="Model Provider"
        required
        validationState={errors.modelProvider ? 'error' : undefined}
        validationMessage={errors.modelProvider?.message}
      >
        <Controller
          name="modelProvider"
          control={control}
          render={({ field }) => (
            <Input
              {...field}
              data-testid="agent-register-form-modelProvider"
              value={field.value ?? ''}
              placeholder="e.g. Anthropic, Azure OpenAI"
              disabled={registerMutation.isPending}
              maxLength={100}
            />
          )}
        />
      </Field>

      <Field
        label="Model Name"
        required
        validationState={errors.modelName ? 'error' : undefined}
        validationMessage={errors.modelName?.message}
      >
        <Controller
          name="modelName"
          control={control}
          render={({ field }) => (
            <Input
              {...field}
              data-testid="agent-register-form-modelName"
              value={field.value ?? ''}
              placeholder="e.g. claude-opus-4-8"
              disabled={registerMutation.isPending}
              maxLength={200}
            />
          )}
        />
      </Field>

      <Field
        label="Runtime Provider"
        validationState={errors.runtimeProvider ? 'error' : undefined}
        validationMessage={errors.runtimeProvider?.message}
      >
        <Controller
          name="runtimeProvider"
          control={control}
          render={({ field }) => (
            <Input
              {...field}
              data-testid="agent-register-form-runtimeProvider"
              value={field.value ?? ''}
              placeholder="Hosting runtime (optional)"
              disabled={registerMutation.isPending}
              maxLength={100}
            />
          )}
        />
      </Field>

      <Field
        label="Data Access Level"
        required
        validationState={errors.dataAccessLevel ? 'error' : undefined}
        validationMessage={errors.dataAccessLevel?.message}
      >
        <Controller
          name="dataAccessLevel"
          control={control}
          render={({ field }) => (
            <Dropdown
              value={field.value}
              selectedOptions={[field.value]}
              disabled={registerMutation.isPending}
              onOptionSelect={(_, data) => field.onChange(data.optionValue ?? field.value)}
              onBlur={field.onBlur}
              data-testid="agent-register-form-dataAccessLevel"
            >
              {DATA_ACCESS_LEVEL_OPTIONS.map((v) => (
                <Option key={v} value={v}>
                  {v}
                </Option>
              ))}
            </Dropdown>
          )}
        />
      </Field>

      <Field
        label="Tool Access Level"
        required
        validationState={errors.toolAccessLevel ? 'error' : undefined}
        validationMessage={errors.toolAccessLevel?.message}
      >
        <Controller
          name="toolAccessLevel"
          control={control}
          render={({ field }) => (
            <Dropdown
              value={field.value}
              selectedOptions={[field.value]}
              disabled={registerMutation.isPending}
              onOptionSelect={(_, data) => field.onChange(data.optionValue ?? field.value)}
              onBlur={field.onBlur}
              data-testid="agent-register-form-toolAccessLevel"
            >
              {TOOL_ACCESS_LEVEL_OPTIONS.map((v) => (
                <Option key={v} value={v}>
                  {v}
                </Option>
              ))}
            </Dropdown>
          )}
        />
      </Field>

      <Field label="MCP Enabled" hint="Agent integrates via MCP">
        <Controller
          name="mcpEnabled"
          control={control}
          render={({ field }) => (
            <Switch
              checked={field.value}
              onChange={(_, data) => field.onChange(data.checked)}
              disabled={registerMutation.isPending}
              label={field.value ? 'Enabled' : 'Disabled'}
              data-testid="agent-register-form-mcpEnabled"
            />
          )}
        />
      </Field>

      <Field label="Capabilities" hint="Add capability tags">
        <Controller
          name="capabilities"
          control={control}
          render={({ field }) => (
            <CapabilitiesTagInput value={field.value ?? []} onChange={field.onChange} disabled={registerMutation.isPending} />
          )}
        />
      </Field>

      <Field
        label="Metadata (JSON)"
        validationState={errors.metadataJson ? 'error' : undefined}
        validationMessage={errors.metadataJson?.message}
      >
        <Controller
          name="metadataJson"
          control={control}
          render={({ field }) => (
            <Textarea
              {...field}
              data-testid="agent-register-form-metadataJson"
              value={field.value ?? ''}
              placeholder="Optional JSON metadata"
              disabled={registerMutation.isPending}
              resize="vertical"
            />
          )}
        />
      </Field>

      <FormActions
        mode="create"
        testIdPrefix="agent-register-form"
        isSubmitting={registerMutation.isPending}
        onCancel={onCancel}
        submitLabel="Register Agent"
        submittingLabel="Registering…"
        submitIcon={<AddRegular />}
      />
    </form>
  );
};
